import React, { useState } from 'react';
import { TrashIcon } from '@heroicons/react/24/outline';
import type { EstadiaCliente, Movimentacao } from '../../../models';
import { useEstadiaClienteMutations } from '../../../hooks/data';
import {
  STATUS_ESTADIA_CLIENTE_LABELS,
  STATUS_ESTADIA_CLIENTE_BADGE_CLASS,
  TIPO_CHAMADO_CLIENTE_LABELS,
  PROCESSO_MOVIMENTACAO_LABELS,
  METODO_ENTREGA_LABELS,
} from '../equipamentosListPage.definitions';
import RegistrarChamadoClienteModal from './RegistrarChamadoClienteModal';
import RecolherParaRaroModal from './RecolherParaRaroModal';
import EditarEstadiaModal from './EditarEstadiaModal';
import ConfirmarChegadaModal from './ConfirmarChegadaModal';
import DestinoColaboradorInfo from './DestinoColaboradorInfo';
import { normalizarPatrimonio } from '../../../models/equipamento.utils';

function formatarData(timestamp: Movimentacao['dataEnvio']): string | null {
  if (!timestamp) return null;
  const data = 'toDate' in timestamp ? timestamp.toDate() : new Date(timestamp as unknown as string);
  return data.toLocaleDateString('pt-BR');
}

interface EstadiaClienteCardProps {
  estadia: EstadiaCliente;
  patrimonioEquipamento: string | null;
  modeloEquipamento: string | null;
  nSerieEquipamento: string | null;
  nomeCliente: string | null;
  nomeColaboradorCotado: string | null;
}

/**
 * Um card por EstadiaCliente (Fase 1 — preparação de equipamento antes de existir
 * Solicitação, ver estadiaCliente.types.ts). Clicar no card abre a edição completa
 * (EditarEstadiaModal); os botões de ação (linha de baixo) cuidam só das transições de
 * status/encerramento, com `stopPropagation` pra não abrir a edição junto. A
 * sincronização de `Equipamento.statusCustodia` acontece automaticamente por Cloud
 * Function (onEstadiaClienteWriteSyncEquipamentoCustody) assim que o `status` muda aqui —
 * este componente nunca escreve em si_equipamentos diretamente.
 */
export default function EstadiaClienteCard({
  estadia,
  patrimonioEquipamento,
  modeloEquipamento,
  nSerieEquipamento,
  nomeCliente,
  nomeColaboradorCotado,
}: EstadiaClienteCardProps) {
  const { atualizarEstadia, removerEstadia } = useEstadiaClienteMutations();
  const [chamadoModalAberto, setChamadoModalAberto] = useState(false);
  const [recolherModalAberto, setRecolherModalAberto] = useState(false);
  const [editarModalAberto, setEditarModalAberto] = useState(false);
  const [confirmarChegadaModalAberto, setConfirmarChegadaModalAberto] = useState(false);

  const encerrada = estadia.status === 'encerrada';
  // "Remoção de Imagem" não tem "próximo destino" no Cliente — só é recolhida de volta pra
  // Raro depois, nunca fica "pronta" aguardando um colaborador nesse mesmo Cliente.
  const podeMarcarPronta = estadia.motivo !== 'remocao_imagem';

  const handleAbrirEdicao = () => setEditarModalAberto(true);
  const handleAbrirEdicaoPorTeclado = (evento: React.KeyboardEvent) => {
    if (evento.key === 'Enter' || evento.key === ' ') {
      evento.preventDefault();
      setEditarModalAberto(true);
    }
  };

  const handleMarcarPronta = (evento: React.MouseEvent) => {
    evento.stopPropagation();
    atualizarEstadia.mutate({ id: estadia.id, status: 'pronta_aguardando_destino' });
  };

  // Exclusão física de verdade — diferente de si_equipamentos, este registro é só o
  // rastreamento da passagem, não o Equipamento em si (ver firestore.rules). Se a estadia
  // ainda estava ativa, a Cloud Function reseta a custódia do Equipamento pro estoque.
  const handleRemover = (evento: React.MouseEvent) => {
    evento.stopPropagation();
    if (!window.confirm('Remover este registro? Esta ação não pode ser desfeita.')) return;
    removerEstadia.mutate(estadia.id);
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleAbrirEdicao}
      onKeyDown={handleAbrirEdicaoPorTeclado}
      className="cursor-pointer rounded-md border border-gray-200 bg-white p-4 hover:border-indigo-300"
    >
      <div className="flex items-center justify-between gap-2">
        <div>
          {/* "BP001 | Lenovo ThinkPad E14 | AKS5652" — modelo/série em peso normal, só
              quando existem (vêm do Equipamento, geralmente preenchidos pelo GLPI). */}
          <p className="font-montserrat text-base text-gray-900">
            <span className="font-bold">{normalizarPatrimonio(patrimonioEquipamento) ?? 'Sem patrimônio'}</span>
            {[modeloEquipamento, nSerieEquipamento].filter(Boolean).map((valor) => (
              <React.Fragment key={valor}>
                <span className="mx-1.5 text-gray-300">|</span>
                <span className="text-sm text-gray-600">{valor}</span>
              </React.Fragment>
            ))}
          </p>
          <p className="text-sm text-gray-500">
            <span className="font-semibold text-gray-900">Cliente:</span> {nomeCliente ?? '—'}
          </p>
          <p className="text-sm text-gray-500">
            <span className="font-semibold text-gray-900">Motivo:</span> {PROCESSO_MOVIMENTACAO_LABELS[estadia.motivo]}
          </p>
          {!encerrada && (
            <p className="text-sm text-gray-500">
              <span className="font-semibold text-gray-900">Colaborador cotado:</span> {nomeColaboradorCotado ?? '—'}
            </p>
          )}
          {estadia.formaEnvio && (
            <p className="text-sm text-gray-500">
              <span className="font-semibold text-gray-900">Envio:</span> {METODO_ENTREGA_LABELS[estadia.formaEnvio]}
              {estadia.codigoRastreio ? ` · ${estadia.codigoRastreio}` : ''}
            </p>
          )}
          {(formatarData(estadia.dataEnvioRaro) || formatarData(estadia.dataChegadaCliente)) && (
            <p className="text-sm text-gray-500">
              <span className="font-semibold text-gray-900">Enviada em:</span>{' '}
              {formatarData(estadia.dataEnvioRaro) ?? 'a definir'} ·{' '}
              <span className="font-semibold text-gray-900">Recebida em:</span>{' '}
              {formatarData(estadia.dataChegadaCliente) ?? 'a definir'}
            </p>
          )}
          {encerrada && estadia.motivoEncerramento === 'retornado_para_raro' && estadia.formaRecolhimento && (
            <p className="text-sm text-gray-500">
              <span className="font-semibold text-gray-900">Recolhimento:</span>{' '}
              {METODO_ENTREGA_LABELS[estadia.formaRecolhimento]}
              {estadia.codigoRastreioRecolhimento ? ` · ${estadia.codigoRastreioRecolhimento}` : ''}
              {formatarData(estadia.dataRecolhimento) ? ` · ${formatarData(estadia.dataRecolhimento)}` : ''}
            </p>
          )}
          {encerrada &&
            estadia.motivoEncerramento === 'enviado_ao_colaborador' &&
            estadia.solicitacaoRealId &&
            estadia.movimentacaoRealId && (
              <DestinoColaboradorInfo
                solicitacaoId={estadia.solicitacaoRealId}
                movimentacaoId={estadia.movimentacaoRealId}
              />
            )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            className="text-gray-400 hover:text-red-600"
            onClick={handleRemover}
            title="Remover registro"
          >
            <TrashIcon className="h-4 w-4" />
          </button>
          <span
            className={`rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS_ESTADIA_CLIENTE_BADGE_CLASS[estadia.status]}`}
          >
            {STATUS_ESTADIA_CLIENTE_LABELS[estadia.status]}
          </span>
        </div>
      </div>

      {estadia.chamados.length > 0 && (
        <div className="mt-2 space-y-1">
          <p className="text-xs font-medium uppercase text-gray-400">Chamados no Cliente</p>
          {estadia.chamados.map((chamado) => (
            <p key={chamado.id} className="text-sm text-gray-600">
              <span className="font-semibold text-gray-900">Chamado:</span> {TIPO_CHAMADO_CLIENTE_LABELS[chamado.tipo]}
              {chamado.numero ? ` — ${chamado.numero}` : ''}
            </p>
          ))}
        </div>
      )}

      {!encerrada && (
        <div className="mt-3 flex flex-wrap gap-2">
          {estadia.status === 'em_transito' && (
            <button
              type="button"
              className="btn-secondary text-sm"
              onClick={(evento) => {
                evento.stopPropagation();
                setConfirmarChegadaModalAberto(true);
              }}
            >
              Confirmar chegada
            </button>
          )}
          {estadia.status === 'em_homologacao' && podeMarcarPronta && (
            <button type="button" className="btn-secondary text-sm" onClick={handleMarcarPronta}>
              Marcar como pronta
            </button>
          )}
          <button
            type="button"
            className="btn-secondary text-sm"
            onClick={(evento) => {
              evento.stopPropagation();
              setChamadoModalAberto(true);
            }}
          >
            Adicionar chamado
          </button>
          <button
            type="button"
            className="text-sm text-gray-500 underline"
            onClick={(evento) => {
              evento.stopPropagation();
              setRecolherModalAberto(true);
            }}
          >
            Recolher para Raro
          </button>
        </div>
      )}

      <RegistrarChamadoClienteModal
        isOpen={chamadoModalAberto}
        onClose={() => setChamadoModalAberto(false)}
        estadia={estadia}
      />
      <RecolherParaRaroModal
        isOpen={recolherModalAberto}
        onClose={() => setRecolherModalAberto(false)}
        estadia={estadia}
      />
      <EditarEstadiaModal
        isOpen={editarModalAberto}
        onClose={() => setEditarModalAberto(false)}
        estadia={estadia}
      />
      <ConfirmarChegadaModal
        isOpen={confirmarChegadaModalAberto}
        onClose={() => setConfirmarChegadaModalAberto(false)}
        estadia={estadia}
      />
    </div>
  );
}
